import { useEffect, useState } from "react";
import api from "@/lib/api";
import { eur } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ShoppingCart, Package as PkgIcon, RefreshCw, Gift } from "lucide-react";

const PAYMENT_METHODS = ["Contanti", "Carta", "Bonifico"];
const toDays = (value, unit) => Math.max(0, (unit === "months" ? (+value || 0) * 30 : (+value || 0)) | 0);

export function DirectSaleDialog({ clientId, onDone }) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState({ packages: [], subscriptions: [] });
  const [type, setType] = useState("package");
  const [refId, setRefId] = useState("");
  const [amount, setAmount] = useState(50);
  const [gcValidity, setGcValidity] = useState({ value: 0, unit: "months" });
  const [method, setMethod] = useState("Contanti");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/packages").then((r) => setCatalog(r.data)).catch(() => {});
  }, [open]);

  const items = type === "package" ? catalog.packages : type === "subscription" ? catalog.subscriptions : [];
  const selected = items.find((i) => i.id === refId);
  const price = type === "giftcard" ? (+amount || 0) : (selected ? +selected.price || 0 : 0);

  const submit = async () => {
    if (type !== "giftcard" && !refId) { toast.error("Seleziona un articolo"); return; }
    if (type === "giftcard" && (+amount || 0) <= 0) { toast.error("Inserisci un importo valido"); return; }
    setBusy(true);
    try {
      const body = { item_type: type, payment_method: method };
      if (type === "giftcard") { body.amount = +amount; body.validity_days = toDays(gcValidity.value, gcValidity.unit); }
      else { body.ref_id = refId; }
      await api.post(`/clients/${clientId}/direct-sale`, body);
      toast.success("Vendita registrata e assegnata alla cliente");
      setOpen(false); setRefId(""); setAmount(50); setGcValidity({ value: 0, unit: "months" });
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nella vendita"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="direct-sale-button"><ShoppingCart className="h-4 w-4 mr-1.5" /> Vendita diretta</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="direct-sale-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Vendita diretta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "package", label: "Pacchetto", icon: PkgIcon },
                { v: "subscription", label: "Abbonamento", icon: RefreshCw },
                { v: "giftcard", label: "Gift Card", icon: Gift },
              ].map((t) => {
                const Icon = t.icon; const active = type === t.v;
                return (
                  <button key={t.v} onClick={() => { setType(t.v); setRefId(""); }} data-testid={`direct-sale-type-${t.v}`}
                    className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-xs font-medium transition-colors ${active ? "border-[hsl(var(--hi-gold))] bg-[hsl(var(--hi-gold)/0.08)] text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                    <Icon className="h-5 w-5" /> {t.label}
                  </button>
                );
              })}
            </div>

            {type !== "giftcard" ? (
              <div className="space-y-1.5">
                <Label>{type === "package" ? "Pacchetto" : "Abbonamento"}</Label>
                <Select value={refId} onValueChange={setRefId}>
                  <SelectTrigger data-testid="direct-sale-item"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {items.length === 0 && <div className="px-2 py-1.5 text-sm text-muted-foreground">Nessun elemento disponibile</div>}
                    {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} · {eur(i.price)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <>
                <div className="space-y-1.5"><Label>Importo Gift Card (€)</Label><Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="direct-sale-amount" /></div>
                <div className="space-y-1.5">
                  <Label>Validità <span className="text-muted-foreground font-normal">(opzionale, 0 = nessuna scadenza)</span></Label>
                  <div className="flex gap-2">
                    <Input type="number" min="0" className="flex-1" value={gcValidity.value} onChange={(e) => setGcValidity({ ...gcValidity, value: e.target.value })} data-testid="direct-sale-gc-validity" />
                    <Select value={gcValidity.unit} onValueChange={(v) => setGcValidity({ ...gcValidity, unit: v })}>
                      <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="days">Giorni</SelectItem><SelectItem value="months">Mesi</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            <div className="space-y-1.5">
              <Label>Metodo di pagamento</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="direct-sale-method"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between rounded-lg bg-muted/60 px-4 py-3">
              <span className="text-sm text-muted-foreground">Totale da incassare</span>
              <span className="font-heading text-2xl font-semibold" data-testid="direct-sale-total">{eur(price)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annulla</Button>
            <Button onClick={submit} disabled={busy} data-testid="direct-sale-confirm">
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Registra vendita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
